import _regeneratorRuntime from 'babel-runtime/regenerator';
import _typeof from 'babel-runtime/helpers/typeof';
import _asyncToGenerator from 'babel-runtime/helpers/asyncToGenerator';
import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import axios from 'axios';
import { AuthInterceptor } from '../interceptors/AuthInterceptor';
import { GeneralErrorsInterceptor } from '../interceptors/GeneralErrorsInterceptor';
import KeycloakService from './keycloak/KeycloakService';
import UserSettingsService from './UserSettingsService';

/** Methods to interact with the REST, import, and vehicle services */

var RestService = function () {
  /**
   * Initialize a custom axios instance
   */
  function RestService() {
    _classCallCheck(this, RestService);

    this.URL_CREATE_IMPORT_JOB = '/jobs/upload';
    this.URL_CLOSE_CASES_JOB = '/batch/closeAll';

    var authInterceptor = new AuthInterceptor(new KeycloakService());
    this.allRestClients = [];
    this.casesRestClient = axios.create({
      // eslint-disable-next-line
      baseURL: CASES_BASE_URL_V1 // defined in webpack config
    });
    this.allRestClients.push(this.casesRestClient);
    this.casesRestClientV2 = axios.create({
      // eslint-disable-next-line
      baseURL: CASES_BASE_URL_V2 // defined in webpack config
    });
    this.allRestClients.push(this.casesRestClientV2);
    this.adminRestClient = axios.create({
      // eslint-disable-next-line
      baseURL: ADMIN_BASE_URL_V1 // defined in webpack config
    });
    this.allRestClients.push(this.adminRestClient);
    this.chartsRestClient = axios.create({
      // eslint-disable-next-line
      baseURL: CHARTS_BASE_URL_V1 // defined in webpack config
    });
    this.allRestClients.push(this.chartsRestClient);
    this.keycloakClient = axios.create({
      baseURL: KeycloakService.getKeycloakAuthUrl()
    });
    this.allRestClients.push(this.keycloakClient);
    this.setRequestInterceptorsToAllClients(authInterceptor.authBearerHeader, authInterceptor.authBearerHeaderError);
    this.setResponseInterceptorsToAllClients(GeneralErrorsInterceptor.passthrough, GeneralErrorsInterceptor.genericErrorsHandler);
  }

  _createClass(RestService, [{
    key: 'setRequestInterceptorsToAllClients',
    value: function setRequestInterceptorsToAllClients(successCallback, errorCallback) {
      this.allRestClients.forEach(function (client) {
        return client.interceptors.request.use(successCallback, errorCallback);
      });
    }
  }, {
    key: 'setResponseInterceptorsToAllClients',
    value: function setResponseInterceptorsToAllClients(successCallback, errorCallback) {
      this.allRestClients.forEach(function (client) {
        return client.interceptors.response.use(successCallback, errorCallback);
      });
    }
  }, {
    key: 'getAllUsers',
    value: function getAllUsers() {
      var realm = KeycloakService.getUsersRealm();
      var url = 'admin/realms/' + realm + '/users';
      var requestConfig = {};
      return this.keycloakClient.get(url, requestConfig);
    }
  }, {
    key: 'getUsersByUsername',
    value: function getUsersByUsername(username) {
      var realm = KeycloakService.getUsersRealm();
      var url = 'admin/realms/' + realm + '/users?username=' + username;
      var requestConfig = {};
      return this.keycloakClient.get(url, requestConfig);
    }
  }, {
    key: 'getUsersCount',
    value: function getUsersCount() {
      var realm = KeycloakService.getUsersRealm();
      var url = 'admin/realms/' + realm + '/users/count';
      var requestConfig = {};
      return this.keycloakClient.get(url, requestConfig);
    }
  }, {
    key: 'cleanup',
    value: function cleanup() {
      return this.adminRestClient.get('/logout/cleanup');
    }
  }, {
    key: 'getUsers',
    value: function getUsers(page, usersPerPage, search) {
      var realm = KeycloakService.getUsersRealm();
      var offsetedPage = page - 1;
      var url = 'admin/realms/' + realm + '/users?max=' + usersPerPage + '&first=' + offsetedPage * usersPerPage;
      if (search) {
        url += '&search=' + search;
      }
      var requestConfig = {};
      return this.keycloakClient.get(url, requestConfig);
    }
  }, {
    key: 'addNewUser',
    value: function addNewUser(user) {
      var realm = KeycloakService.getUsersRealm();
      var url = 'admin/realms/' + realm + '/users';
      return this.keycloakClient.post(url, user);
    }
  }, {
    key: 'addUserRole',
    value: function addUserRole(userId, clientId, rolesArray) {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/users/' + userId + '/role-mappings/clients/' + clientId;
      return this.keycloakClient.post(url, rolesArray);
    }
  }, {
    key: 'getUserRoles',
    value: function getUserRoles(userId, clientId) {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/users/' + userId + '/role-mappings/clients/' + clientId;
      return this.keycloakClient.get(url);
    }
  }, {
    key: 'deleteUserRoles',
    value: function deleteUserRoles(userId, clientId, rolesArray) {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/users/' + userId + '/role-mappings/clients/' + clientId;
      // axios client delete method does not have data parameter therefore data needs to be passed as a part of the config parameter.
      return this.keycloakClient.delete(url, { data: rolesArray });
    }
  }, {
    key: 'getClientRole',
    value: function getClientRole(clientId, roleName) {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/clients/' + clientId + '/roles/' + roleName;
      var requestConfig = {};
      return this.keycloakClient.get(url, requestConfig);
    }
  }, {
    key: 'getAllClients',
    value: function getAllClients() {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/clients';
      return this.keycloakClient.get(url);
    }
  }, {
    key: 'getAllRoles',
    value: function getAllRoles(clientId) {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/clients/' + clientId + '/roles';
      return this.keycloakClient.get(url);
    }
  }, {
    key: 'sendVerificationEmail',
    value: function sendVerificationEmail(userId) {
      var realm = KeycloakService.getUsersRealm();
      var url = '/admin/realms/' + realm + '/users/' + userId + '/send-verify-email';
      return this.keycloakClient.put(url);
    }
  }, {
    key: 'editUser',
    value: function editUser(user) {
      var realm = KeycloakService.getUsersRealm();
      var url = 'admin/realms/' + realm + '/users/' + user.id;
      return this.keycloakClient.put(url, user);
    }
  }, {
    key: 'deleteUser',
    value: function deleteUser(user) {
      var realm = KeycloakService.getUsersRealm();
      var url = 'admin/realms/' + realm + '/users/' + user.id;
      return this.keycloakClient.delete(url);
    }
  }, {
    key: 'getMyUserName',
    value: function getMyUserName() {
      return this.casesRestClient.get('user/my-user-name');
    }

    /**
     * Get a list of policy cases
     * @param {number} page - the page number (starting at 1)
     * @param {number} size - size of the page (defaults to 10)
     * @param {Object} filter - a map of query param names to values
     */

  }, {
    key: 'getCaseList',
    value: function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(page, size, filter) {
        var url, channel, key, item;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                url = 'policycase?page=' + page + '&size=' + size;
                channel = UserSettingsService.getUserChannel();

                if (channel) {
                  url += '&channel=' + channel;
                }
                if (typeof filter !== 'undefined') {
                  for (key in filter) {
                    if (typeof filter[key] === 'string' && String(filter[key]).length > 0) {
                      url += '&' + key + '=' + filter[key];
                    } else if (_typeof(filter[key]) === 'object' && filter[key].length > 0) {
                      for (item in filter[key]) {
                        url += '&' + key + '=' + filter[key][item];
                      }
                    }
                  }
                }
                return _context.abrupt('return', this.casesRestClient.get(url));

              case 5:
              case 'end':
                return _context.stop();
            }
          }
        }, _callee, this);
      }));

      function getCaseList(_x, _x2, _x3) {
        return _ref.apply(this, arguments);
      }

      return getCaseList;
    }()

    /**
     * Create a new policy case
     * @param {Object} requestObject - the case object
     * @param {Object} caseHeaderId - when recalculating, the previous case header id
     * @param {boolean} save - true if the new case should be persisted
     * @param {boolean} save - true if create new policy
     * * @param {boolean} customerUniqueId - send if create new policy
     */

  }, {
    key: 'createNewCase',
    value: function createNewCase(requestObject, caseHeaderId, save, isNewPolicy, customerUniqueId) {
      var url = 'policy/case/calculate?';
      if (typeof caseHeaderId !== 'undefined') {
        url += 'caseheaderid=' + caseHeaderId + '&';
      }
      if (typeof requestObject.batchID !== 'undefined') {
        url += 'batchid=' + requestObject.batchID + '&';
      }
      if (typeof isNewPolicy !== 'undefined') {
        url += 'isnewpolicy=' + isNewPolicy + '&';
      }
      if (typeof save !== 'undefined' && !save) {
        url += 'nopersist=true&';
      }
      if (typeof customerUniqueId !== 'undefined') {
        url += 'customer=' + customerUniqueId + '&';
      }
      return this.casesRestClient.request({
        url: url.slice(0, -1),
        method: 'post',
        data: requestObject,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }

    /**
     * Update an existing case
     * @param {string} case header Id - the case ID
     * @param {Object} requestObject - the new case object
     */

  }, {
    key: 'updateCase',
    value: function updateCase(policycaseHeaderId, requestObject) {
      var url = 'policy/case/' + policycaseHeaderId;

      return this.casesRestClient.request({
        url: url,
        method: 'put',
        data: requestObject,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }
  }, {
    key: 'setUserLocked',
    value: function setUserLocked(policycaseHeaderId, userLocked) {
      var url = 'policy/case/' + policycaseHeaderId + '/userLocked/' + userLocked;

      return this.casesRestClient.put(url);
    }
  }, {
    key: 'downloadCase',
    value: function downloadCase(policycaseHeaderId) {
      var url = 'pdf/export/' + policycaseHeaderId;
      return this.casesRestClient.get(url, {
        headers: { accept: 'application/pdf' },
        responseType: 'blob'
      });
    }
  }, {
    key: 'downloadApiLog',
    value: function downloadApiLog(policycaseId, policyCaseApiLogId) {
      var url = 'txt/export/' + policycaseId + '/' + policyCaseApiLogId;
      return this.casesRestClient.get(url, {
        headers: { accept: 'text/plain' },
        responseType: 'blob'
      });
    }
  }, {
    key: 'saveCase',
    value: function saveCase(policaseHeaderId) {
      var url = '/roundcube/saveCase/' + policaseHeaderId;
      return this.casesRestClient.post(url);
    }

    /**
     * Update status of a policycase header and optionally status reason of its active policycase.
     * @param {string} case header Id - the case ID
     * @param {string} status of the policycase header
     * @param {string} statusReason of the active policycase
     */

  }, {
    key: 'updateCaseStaus',
    value: function updateCaseStaus(policycaseHeaderId, status, statusReason) {
      var url = 'policycase/' + policycaseHeaderId + '/status';
      var requestObject = { statusReason: statusReason, status: status };

      return this.casesRestClient.request({
        url: url,
        method: 'put',
        data: requestObject,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }
  }, {
    key: 'setSelectedPolicyOfPolicycase',
    value: function setSelectedPolicyOfPolicycase(policycaseHeaderId, policyId) {
      var url = 'policycase/' + policycaseHeaderId + '/selected/' + policyId;
      return this.casesRestClient.request({
        url: url,
        method: 'put',
        headers: {
          'Content-type': 'application/json'
        }
      });
    }

    /**
     * Get all import jobs
     */

  }, {
    key: 'getImportJobs',
    value: function getImportJobs() {
      return this.adminRestClient.get('/jobs');
    }

    /**
     * Get all running/pending jobs
     */

  }, {
    key: 'getRunningAndPendingJobs',
    value: function getRunningAndPendingJobs() {
      return this.adminRestClient.get('/jobs?status=RUNNING&status=PENDING');
    }

    /**
     * Get a single import job
     * @param {string} jobId - the job ID
     */

  }, {
    key: 'getJob',
    value: function getJob(jobId) {
      return this.adminRestClient.get('/jobs/' + jobId);
    }
  }, {
    key: 'isSomeJobRowStillRunning',
    value: function isSomeJobRowStillRunning(jobRowId) {
      return this.adminRestClient.get('/jobs/rows/' + jobRowId + '/isRunning');
    }

    /**
     * Get errors for an import job
     * @param {string} jobId - the job ID
     */

  }, {
    key: 'getErrors',
    value: function getErrors(jobId) {
      return this.adminRestClient.get('/jobs/' + jobId + '/errors');
    }

    /**
     * Get completed rows for an import job
     * @param {string} jobId - the job ID
     */

  }, {
    key: 'getCompleted',
    value: function getCompleted(jobId) {
      return this.adminRestClient.get('/jobs/' + jobId + '/completed');
    }
  }, {
    key: 'getCompletedCaseId',
    value: function getCompletedCaseId(uniqueId) {
      return this.casesRestClient.get('/policycase/' + uniqueId + '/completedUniqueId');
    }

    /**
    * Get completed rows for an import job
    * @param {string} jobId - the job ID
    */

  }, {
    key: 'getSkipped',
    value: function getSkipped(jobId) {
      return this.adminRestClient.get('/jobs/' + jobId + '/skipped');
    }
  }, {
    key: 'getHistoryPolicycases',
    value: function getHistoryPolicycases(policaseHeaderId) {
      return this.casesRestClient.get('policy/case-history/' + policaseHeaderId);
    }
  }, {
    key: 'getApiLog',
    value: function getApiLog(policyCaseId) {
      return this.casesRestClient.get('policycase/api-log/' + policyCaseId);
    }
  }, {
    key: 'updateCustomer',
    value: function updateCustomer(requestObject) {
      var url = 'customer';

      return this.casesRestClient.request({
        url: url,
        method: 'put',
        data: requestObject,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }

    /**
     * URL for creation of a new import job
     */


    /**
     * URL for uploading a file with list of cases to be closed.
     */

  }, {
    key: 'postToServer',


    /**
     * General function for posting a data to the server. A URL parameter should be used always as a constant within this file.
     * @param {Object} url - the url to be used for this request
     * @param {Object} formData - the form data
     */
    value: function postToServer(url, formData) {
      return this.adminRestClient.post(url, formData);
    }
  }, {
    key: 'postToCaseServer',
    value: function postToCaseServer(url, formData) {
      return this.casesRestClient.post(url, formData);
    }
  }, {
    key: 'getFromCaseServer',
    value: function getFromCaseServer(policyId) {
      return this.casesRestClient.post('/roundcube/' + policyId + '/calculate');
    }

    /**
     * Change the status of an import job
     * @param {string} jobId - the job ID
     * @param {Object} status - the new status
     */

  }, {
    key: 'changeJobStatus',
    value: function changeJobStatus(jobId, status, productLine) {
      return this.adminRestClient.put('/jobs/' + jobId + '?status=' + status + '&productLine=' + productLine);
    }

    /**
     * Cancel all pending jobs
     */

  }, {
    key: 'cancelJobQueue',
    value: function cancelJobQueue() {
      return this.adminRestClient.put('/jobs/cancel-queue');
    }

    /**
     * Cancel a pending or paused job
     */

  }, {
    key: 'cancelJob',
    value: function cancelJob(jobId) {
      return this.adminRestClient.put('/jobs/cancel/' + jobId);
    }

    /**
     * Cancel a pending action
     */

  }, {
    key: 'cancelAction',
    value: function cancelAction(actionId) {
      return this.adminRestClient.put('/actions/cancel/' + actionId);
    }

    /**
     * Delete an import job
     * @param {string} jobId - the job ID
     */

  }, {
    key: 'deleteJob',
    value: function deleteJob(jobId) {
      return this.adminRestClient.delete('/jobs/' + jobId);
    }

    /**
     * Get all case batches (batchID, case type, status combinations)
     */

  }, {
    key: 'getBatches',
    value: function getBatches() {
      return this.casesRestClientV2.get('/batch/batchItems'); // This one goes directly to cases
    }

    /**
     * Get all customer batches (batchID, count)
     */

  }, {
    key: 'getCustomerBatches',
    value: function getCustomerBatches() {
      return this.casesRestClientV2.get('/batch/customerBatchItems');
    }

    /**
     * Recalculate a batch
     * @param {string} batchId - the batch ID
     */

  }, {
    key: 'recalculateBatch',
    value: function recalculateBatch(batchId, productLine, status) {
      return this.adminRestClient.post('/batch/recalculate?batchId=' + batchId + '&productLine=' + productLine + '&status=' + status);
    }

    /**
     * Close cases by batchId, productLine, and status
     * @param {string} batchId - the batch ID
     * @param {string} productLine - the product line
     * @param {string} status - the case status
     */

  }, {
    key: 'closeBatch',
    value: function closeBatch(batchId, productLine, status) {
      return this.adminRestClient.post('/batch/close?batchId=' + batchId + '&productLine=' + productLine + '&status=' + status);
    }

    /**
     * Get batch actions filtered by type with pagination
     * @param {string} actionTypes - array of action types
     * @param {number} page - the offset aka. pagination page
     * @param {number} size - the page size
     */

  }, {
    key: 'getBatchActions',
    value: function getBatchActions(actionTypes, page, size) {
      var url = '/actions?';
      if (actionTypes && actionTypes.length > 0) {
        actionTypes.forEach(function (type) {
          url += 'type=' + type + '&';
        });
      }
      if (page) url += 'page=' + page + '&';
      if (size) url += 'size=' + size;
      return this.adminRestClient.get(url);
    }

    /**
     * Count batch actions filtered by type
     * @param {string} actionTypes - array of action types
     */

  }, {
    key: 'countBatchActions',
    value: function countBatchActions(actionTypes) {
      var url = '/actions/count';
      if (actionTypes && actionTypes.length > 0) {
        url += '?';
        actionTypes.forEach(function (type) {
          url += 'type=' + type + '&';
        });
        url = url.slice(0, -1);
      }
      return this.adminRestClient.get(url);
    }

    /**
     * Save a batch action in the db
     * @param {string} actionType - the type of the action
     * @param {Array} batches - the batches as array of objects
     */

  }, {
    key: 'sendBatchAction',
    value: function sendBatchAction(actionType, batches) {
      if (typeof actionType === 'undefined') {
        return;
      }
      var url = '/actions/add/';

      if (actionType === 'delete') {
        url += 'delete/cases-batches';
      } else if (actionType === 'delete_customers') {
        url += 'delete/customer-batches';
      } else {
        url += actionType;
      }

      return this.adminRestClient.request({
        url: url,
        method: 'post',
        data: batches,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }

    /**
     * Save a case list action in the db
     * @param {string} actionType - the type of the action
     * @param {File} file - file containing list of case ids
     */

  }, {
    key: 'sendCaseListAction',
    value: function sendCaseListAction(actionType, file) {
      if (typeof actionType === 'undefined') {
        return;
      }

      var formData = new FormData();
      formData.append('cases_list_file', file);

      return this.adminRestClient.request({
        url: '/actions/add/' + actionType + '/cases-list',
        method: 'post',
        data: formData,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }

    /**
     * Get all transformation files for running an export
     */

  }, {
    key: 'getExportFiles',
    value: function getExportFiles() {
      return this.adminRestClient.get('/export/');
    }

    /**
     * Get all export output files
     */

  }, {
    key: 'getOutputFiles',
    value: function getOutputFiles() {
      return this.adminRestClient.get('/export/output');
    }

    /**
     * Execute a transformation/export
     * @param {string} fileName - the transformation file to run
     * @param {Object} params - a map of parameters to pass to the transformation
     */

  }, {
    key: 'executeTransformation',
    value: function executeTransformation(fileName, params) {
      var url = '/export/execute?file=' + fileName;
      if (typeof params !== 'undefined' && params.length > 0) {
        for (var i = 0; i < params.length; i++) {
          url += '&' + params[i].name + '=' + params[i].value;
        }
      }
      return this.adminRestClient.get(url);
    }

    /**
     * Get details about a transformation file (parameters, simple name etc.)
     * @param {string} fileName - the transformation file name
     */

  }, {
    key: 'getTransformationFileData',
    value: function getTransformationFileData(fileName) {
      var url = '/export/params?file=' + fileName;
      return this.adminRestClient.get(url);
    }

    /**
     * Delete an output file
     * @param {string} fileName - the name of the output file
     */

  }, {
    key: 'deleteOutputFile',
    value: function deleteOutputFile(fileName) {
      var url = '/export/delete?file=' + fileName;
      return this.adminRestClient.delete(url);
    }

    /**
     * Get all productInsurers mapped by ProductLine
     */

  }, {
    key: 'getAllProductInsurersMappedByProductLine',
    value: function getAllProductInsurersMappedByProductLine() {
      return this.casesRestClient.get('/settings/enabled-insurers/byProductLine');
    }

    /**
     * Get all cover types
     */

  }, {
    key: 'getAllCoverTypes',
    value: function getAllCoverTypes() {
      return this.casesRestClientV2.get('/cover-category');
    }

    /**
     * Get all insurer names
     */

  }, {
    key: 'getAllInsurerNames',
    value: function getAllInsurerNames() {
      return this.casesRestClientV2.get('/insurer');
    }

    /**
     * Get all affinity table rows
     */

  }, {
    key: 'getAllAffinityRows',
    value: function getAllAffinityRows() {
      return this.casesRestClientV2.get('/affinity');
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createAffinityRow',
    value: function createAffinityRow(row) {
      return this.casesRestClientV2.post('/affinity', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateAffinityRow',
    value: function updateAffinityRow(row) {
      return this.casesRestClientV2.put('/affinity', row);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteAffinityRow',
    value: function deleteAffinityRow(id) {
      return this.casesRestClientV2.delete('/affinity/' + id);
    }

    /**
     * Get all affinity table rows
     */

  }, {
    key: 'getAllMappingRows',
    value: function getAllMappingRows() {
      return this.casesRestClientV2.get('/mapping');
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createMappingRow',
    value: function createMappingRow(row) {
      return this.casesRestClientV2.post('/mapping', row);
    }

    /**
     * Create a new affinity table row with excel data
     * @param file - { multipart file }
     */

  }, {
    key: 'createExcelMappingRow',
    value: function createExcelMappingRow(formData) {
      return this.casesRestClientV2.post('/mapping/createFromExcel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateMappingRow',
    value: function updateMappingRow(row) {
      return this.casesRestClientV2.put('/mapping', row);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteMappingRow',
    value: function deleteMappingRow(id) {
      return this.casesRestClientV2.delete('/mapping/' + id);
    }

    /**
     * Get all affinity table rows
     */

  }, {
    key: 'getAllCoverCategoryRows',
    value: function getAllCoverCategoryRows() {
      return this.casesRestClientV2.get('/cover-category');
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createCoverCategoryRow',
    value: function createCoverCategoryRow(row) {
      return this.casesRestClientV2.post('/cover-category', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateCoverCategoryRow',
    value: function updateCoverCategoryRow(row) {
      return this.casesRestClientV2.put('/cover-category', row);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteCoverCategoryRow',
    value: function deleteCoverCategoryRow(id) {
      return this.casesRestClientV2.delete('/cover-category/' + id);
    }

    /**
     * Get all affinity table rows
     */

  }, {
    key: 'getAllProductlineRows',
    value: function getAllProductlineRows() {
      return this.casesRestClient.get('/productline');
    }
  }, {
    key: 'getAllSubbrancheRows',
    value: function getAllSubbrancheRows() {
      return this.casesRestClient.get('/subbranche');
    }
  }, {
    key: 'getAllContextitemsRows',
    value: function getAllContextitemsRows() {
      return this.casesRestClient.get('/contextitems');
    }
  }, {
    key: 'getAllFunctionitemsRows',
    value: function getAllFunctionitemsRows() {
      return this.casesRestClient.get('/functionitems');
    }
  }, {
    key: 'getAllValuemappingRows',
    value: function getAllValuemappingRows() {
      return this.casesRestClient.get('/valuemapping');
    }
  }, {
    key: 'getAllNoclaimsdiscountRows',
    value: function getAllNoclaimsdiscountRows() {
      return this.casesRestClient.get('/noclaimsdiscount');
    }
  }, {
    key: 'getAllPackagediscountsRows',
    value: function getAllPackagediscountsRows() {
      return this.casesRestClient.get('/packagediscounts');
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createProductlineRow',
    value: function createProductlineRow(row) {
      return this.casesRestClient.post('/productline', row);
    }
  }, {
    key: 'createSubbrancheRow',
    value: function createSubbrancheRow(row) {
      return this.casesRestClient.post('/subbranche', row);
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createContextitemsRow',
    value: function createContextitemsRow(row) {
      return this.casesRestClient.post('/contextitems', row);
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createFunctionitemsRow',
    value: function createFunctionitemsRow(row) {
      return this.casesRestClient.post('/functionitems', row);
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createValuemappingRow',
    value: function createValuemappingRow(row) {
      return this.casesRestClient.post('/valuemapping', row);
    }
  }, {
    key: 'createNoclaimsdiscountRow',
    value: function createNoclaimsdiscountRow(row) {
      return this.casesRestClient.post('/noclaimsdiscount', row);
    }
  }, {
    key: 'createPackagediscountsRow',
    value: function createPackagediscountsRow(row) {
      return this.casesRestClient.post('/packagediscounts', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateProductlineRow',
    value: function updateProductlineRow(row) {
      return this.casesRestClient.put('/productline', row);
    }
  }, {
    key: 'updateSubbrancheRow',
    value: function updateSubbrancheRow(row) {
      return this.casesRestClient.put('/subbranche', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateContextitemsRow',
    value: function updateContextitemsRow(row) {
      return this.casesRestClient.put('/contextitems', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateFunctionitemsRow',
    value: function updateFunctionitemsRow(row) {
      return this.casesRestClient.put('/functionitems', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateValuemappingRow',
    value: function updateValuemappingRow(row) {
      return this.casesRestClient.put('/valuemapping', row);
    }
  }, {
    key: 'updateNoclaimsdiscountRow',
    value: function updateNoclaimsdiscountRow(row) {
      return this.casesRestClient.put('/noclaimsdiscount', row);
    }
  }, {
    key: 'updatePackagediscountsRow',
    value: function updatePackagediscountsRow(row) {
      return this.casesRestClient.put('/packagediscounts', row);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteProductlineRow',
    value: function deleteProductlineRow(id) {
      return this.casesRestClient.delete('/productline/' + id);
    }
  }, {
    key: 'deleteSubbrancheRow',
    value: function deleteSubbrancheRow(id) {
      return this.casesRestClient.delete('/subbranche/' + id);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteContextitemsRow',
    value: function deleteContextitemsRow(id) {
      return this.casesRestClient.delete('/contextitems/' + id);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteFunctionitemsRow',
    value: function deleteFunctionitemsRow(id) {
      return this.casesRestClient.delete('/functionitems/' + id);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteValuemappingRow',
    value: function deleteValuemappingRow(id) {
      return this.casesRestClient.delete('/valuemapping/' + id);
    }
  }, {
    key: 'deleteNoclaimsdiscountRow',
    value: function deleteNoclaimsdiscountRow(id) {
      return this.casesRestClient.delete('/noclaimsdiscount/' + id);
    }
  }, {
    key: 'deletePackagediscountsRow',
    value: function deletePackagediscountsRow(id) {
      return this.casesRestClient.delete('/packagediscounts/' + id);
    }

    /**
     * Get all affinity table rows
     */

  }, {
    key: 'getAllProductRows',
    value: function getAllProductRows() {
      return this.casesRestClient.get('/product');
    }

    /**
     * Create a new affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createProductRow',
    value: function createProductRow(row) {
      return this.casesRestClient.post('/product', row);
    }

    /**
     * Update an existing affinity table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateProductRow',
    value: function updateProductRow(row) {
      return this.casesRestClient.put('/product', row);
    }

    /**
     * Delete an existing affinity table row
     * @param id - of affinity table row
     */

  }, {
    key: 'deleteProductRow',
    value: function deleteProductRow(id) {
      return this.casesRestClient.delete('/product/' + id);
    }

    /**
     * Get all insurer table rows
     */

  }, {
    key: 'getAllInsurerRows',
    value: function getAllInsurerRows() {
      return this.casesRestClientV2.get('/insurer');
    }

    /**
     * Create a new insurer table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createInsurerRow',
    value: function createInsurerRow(row) {
      return this.casesRestClientV2.post('/insurer', row);
    }

    /**
     * Update an existing insurer table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateInsurerRow',
    value: function updateInsurerRow(row) {
      return this.casesRestClientV2.put('/insurer', row);
    }

    /**
     * Delete an existing insurer table row
     * @param id - of insurer table row
     */

  }, {
    key: 'deleteInsurerRow',
    value: function deleteInsurerRow(id) {
      return this.casesRestClientV2.delete('/insurer/' + id);
    }

    /**
     * Update product insurers setting when changed
     */

  }, {
    key: 'updateProductInsurersSetting',
    value: function updateProductInsurersSetting(productInsurersId, settingName, value) {
      return this.casesRestClient.put('/settings/enabled-insurers/' + productInsurersId + '?setting=' + settingName + '&settingValue=' + value);
    }
  }, {
    key: 'getImportTemplateDownloadLink',
    value: function getImportTemplateDownloadLink(productLine) {
      return this.adminRestClient.get('/export/template?productLineName=' + productLine, {
        headers: { accept: 'application/vnd.ms-excel' },
        responseType: 'blob'
      });
    }
  }, {
    key: 'getConfigForm',
    value: function getConfigForm(productLine) {
      return this.adminRestClient.get('/config/form/policycase/productline/' + productLine + '?mode=CURRENT');
    }
  }, {
    key: 'getDefaultConfigForm',
    value: function getDefaultConfigForm(productLine) {
      return this.adminRestClient.get('/config/form/policycase/productline/' + productLine);
    }
  }, {
    key: 'saveCurrentConfigurationsAsDefault',
    value: function saveCurrentConfigurationsAsDefault(configType) {
      return this.adminRestClient.put('/config/form/default/policycase/productline/' + configType);
    }
  }, {
    key: 'getCustomerConfigForm',
    value: function getCustomerConfigForm() {
      return this.adminRestClient.get('/config/form/customer?mode=CURRENT');
    }
  }, {
    key: 'getDefaultCustomerConfigForm',
    value: function getDefaultCustomerConfigForm() {
      return this.adminRestClient.get('/config/form/customer');
    }
  }, {
    key: 'getAllProductLines',
    value: function getAllProductLines() {
      return this.casesRestClient.get('/productline');
    }
  }, {
    key: 'getAllSubbranches',
    value: function getAllSubbranches() {
      return this.casesRestClient.get('/subbranche');
    }
  }, {
    key: 'getAllContextItems',
    value: function getAllContextItems() {
      return this.casesRestClient.get('/contextitems');
    }
  }, {
    key: 'getAllFunctionItems',
    value: function getAllFunctionItems() {
      return this.casesRestClient.get('/functionitems');
    }
  }, {
    key: 'getAllValueMapping',
    value: function getAllValueMapping() {
      return this.casesRestClient.get('/valuemapping');
    }
  }, {
    key: 'getAvailableDistinctBatchIds',
    value: function getAvailableDistinctBatchIds() {
      return this.casesRestClientV2.get('/batch/distinctBatchIds'); // This one goes directly to cases
    }
  }, {
    key: 'getCustomerDistinctBatchIds',
    value: function getCustomerDistinctBatchIds() {
      return this.casesRestClientV2.get('/batch/customerDistinctBatchIds');
    }
  }, {
    key: 'getDownloadableExportFiles',
    value: function getDownloadableExportFiles() {
      return this.adminRestClient.get('/export/listOutputs');
    }
  }, {
    key: 'downloadExportFile',
    value: function downloadExportFile(fileName) {
      return this.adminRestClient.get('/export/outFile/' + fileName, {
        headers: { accept: 'application/octet-stream' },
        responseType: 'blob'
      });
    }
  }, {
    key: 'deleteExportOutput',
    value: function deleteExportOutput(fileName) {
      return this.adminRestClient.delete('/export/outFile/' + fileName);
    }
  }, {
    key: 'updateProductLineEnable',
    value: function updateProductLineEnable(productLine, enableValue) {
      return this.casesRestClient.put('/productLine/' + productLine + '?enableValue=' + enableValue);
    }
  }, {
    key: 'uploadConfigFile',
    value: function uploadConfigFile(productLine, config) {
      return this.adminRestClient.request({
        url: '/config/form/policycase/productline/' + productLine + '?mode=CURRENT',
        method: 'put',
        data: config,
        headers: {
          'Content-type': 'text/plain'
        }
      });
    }
  }, {
    key: 'uploadCustomerConfigFile',
    value: function uploadCustomerConfigFile(config) {
      return this.adminRestClient.request({
        url: '/config/form/customer?mode=CURRENT',
        method: 'put',
        data: config,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }
  }, {
    key: 'getTransformationFile',
    value: function getTransformationFile(productLine) {
      return this.adminRestClient.get('/export/transformation-file?productLineName=' + productLine);
    }
  }, {
    key: 'getPdfTemplateFile',
    value: function getPdfTemplateFile(productLine) {
      return this.adminRestClient.get('/export/pdf-template-file?productLineName=' + productLine);
    }
  }, {
    key: 'generateImportTempalateFile',
    value: function generateImportTempalateFile(filename) {
      return this.adminRestClient.get('/schemaDefination/generate-import-template/' + filename, {
        headers: { accept: 'application/vnd.ms-excel' },
        responseType: 'blob'
      });
    }
  }, {
    key: 'getPolicyCaseStatuses',
    value: function getPolicyCaseStatuses() {
      return this.casesRestClient.get('/status');
    }
  }, {
    key: 'getAllSchemaDefinitions',
    value: function getAllSchemaDefinitions() {
      return this.adminRestClient.get('/schemaDefination');
    }
  }, {
    key: 'deleteSchemaDefinition',
    value: function deleteSchemaDefinition(id) {
      return this.adminRestClient.delete('/schemaDefination/' + id);
    }
  }, {
    key: 'getAllGimObjects',
    value: function getAllGimObjects() {
      return this.casesRestClient.get('/gim-object');
    }
  }, {
    key: 'deleteGimObject',
    value: function deleteGimObject(id) {
      return this.casesRestClient.delete('/gim-object/' + id);
    }
  }, {
    key: 'export',
    value: function _export(batchId, caseStatus, productLine, closeCheckbox) {
      var payload = {};
      payload['batchId'] = batchId;
      payload['caseStatus'] = caseStatus;
      payload['productLineName'] = productLine;
      var additionalParams = {};
      additionalParams['closeCheckbox'] = closeCheckbox;
      payload['additionalParams'] = additionalParams;

      return this.adminRestClient.post('export/execute', payload);
    }
  }, {
    key: 'getCustomerList',
    value: function getCustomerList(page, size, filter) {
      var url = 'customer?page=' + page + '&size=' + size;
      if (typeof filter !== 'undefined') {
        for (var key in filter) {
          if (typeof filter[key] === 'string' && String(filter[key]).length > 0) {
            url += '&' + key + '=' + filter[key];
          } else if (_typeof(filter[key]) === 'object' && filter[key].length > 0) {
            for (var item in filter[key]) {
              url += '&' + key + '=' + filter[key][item];
            }
          }
        }
      }
      return this.casesRestClient.get(url);
    }

    /**
     * Get details for customer.
     * @param id - the customer id
     */

  }, {
    key: 'getCustomerDetails',
    value: function () {
      var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(customerId) {
        var channel, url;
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                channel = UserSettingsService.getUserChannel();
                url = 'customer/details/' + customerId;

                if (channel) {
                  url += '?channel=' + channel;
                }
                return _context2.abrupt('return', this.casesRestClient.get(url));

              case 4:
              case 'end':
                return _context2.stop();
            }
          }
        }, _callee2, this);
      }));

      function getCustomerDetails(_x4) {
        return _ref2.apply(this, arguments);
      }

      return getCustomerDetails;
    }()

    /**
     * Get all channels
     */

  }, {
    key: 'getChannels',
    value: function getChannels() {
      return this.casesRestClient.get('/policycase/channels');
    }

    /**
     * Get the application specific client name.
     */

  }, {
    key: 'getAppClient',
    value: function getAppClient() {
      return this.adminRestClient.get('config/client');
    }
  }, {
    key: 'getConfiguration',
    value: function getConfiguration(configKey) {
      return this.adminRestClient.get('config/properties/' + configKey);
    }
  }, {
    key: 'getAvailableRulesGitBranches',
    value: function getAvailableRulesGitBranches() {
      return this.adminRestClient.get('rules/branches');
    }
  }, {
    key: 'getGitRepoUri',
    value: function getGitRepoUri() {
      return this.adminRestClient.get('rules/git-repo-uri');
    }
  }, {
    key: 'setConfiguration',
    value: function setConfiguration(configKey, configValue) {
      return this.adminRestClient.request({
        url: 'config/properties/' + configKey,
        method: 'put',
        data: configValue,
        headers: {
          'Content-type': 'text/plain'
        }
      });
    }

    /**
     * Get feature toggles from system properties. Format: { cyberCover.enabled: 'true' }
     * @param feature - the name of a feature toggle as written in the server startup options, e.g. 'cyberCover.enabled'
     */

  }, {
    key: 'getFeatureToggle',
    value: function getFeatureToggle(feature) {
      var url = 'config/toggles';
      if (feature) {
        url += '?feature=' + feature;
      }
      return this.adminRestClient.get(url);
    }

    /**
     * Get the maven project versions from the ADMIN module.
     */

  }, {
    key: 'getVersionFromAdminModule',
    value: function getVersionFromAdminModule() {
      return this.adminRestClient.get('config/version');
    }

    /**
     * Get the maven project versions from the CASES module.
     */

  }, {
    key: 'getVersionFromCasesModule',
    value: function getVersionFromCasesModule(module) {
      var url = 'config/version';
      if (module) {
        url += '?module=' + module;
      }
      return this.casesRestClient.get(url);
    }

    /**
     * Get the maven project versions from the CHARTS module.
     */

  }, {
    key: 'getVersionFromChartsModule',
    value: function getVersionFromChartsModule() {
      return this.chartsRestClient.get('config/version');
    }

    /**
     * Get the domain specific language definition file. Contains the filename and file contents.
     */

  }, {
    key: 'getDSLFiles',
    value: function getDSLFiles() {
      return this.adminRestClient.get('rules/dsl');
    }

    /**
     * Get the business rules files list. Contains filename, modifiedDate and creationDate per file.
     */

  }, {
    key: 'getRulesFilesList',
    value: function getRulesFilesList() {
      return this.adminRestClient.get('rules/rdslr-meta');
    }

    /**
     * Get the selected business rule file. Contains the filename and file contents.
     * @param {string} filename - the name of the file to get
     */

  }, {
    key: 'getRuleFile',
    value: function getRuleFile(filename) {
      return this.adminRestClient.get('rules/rdslr/' + filename);
    }

    /**
     * Delete the selected business rule file. Contains the filename and file contents.
     * @param {string} filename - the name of the file to delete
     */

  }, {
    key: 'deleteRuleFile',
    value: function deleteRuleFile(filename) {
      return this.adminRestClient.delete('rules/rdslr/' + filename);
    }

    /**
     *  Save the edited business rule file.
     * @param filename
     * @param fileContent
     * @returns {AxiosPromise}
     */

  }, {
    key: 'saveRuleFile',
    value: function saveRuleFile(filename, fileContent) {
      return this.adminRestClient.request({
        url: 'rules/rdslr/' + filename,
        method: 'put',
        data: fileContent,
        headers: {
          'Content-type': 'text/plain'
        }
      });
    }
  }, {
    key: 'resetDrools',
    value: function resetDrools() {
      return this.adminRestClient.put('rules/reset');
    }
  }, {
    key: 'droolsStatus',
    value: function droolsStatus() {
      return this.adminRestClient.get('rules/reset/status');
    }

    /**
     * Sends the client logs to the backend
     * @param payload with the schema:
     *{
     *     "timeStamp": 1201048234203,
     *     "level": "ERROR",
     *     "url": "http://localhost:8081/new",
     *     "message": "Big problem!",
     *}
     * DO NOT USE THE LOGGER IN THIS FUNCTION as it will create endless cycle
     */

  }, {
    key: 'sendClientLog',
    value: function sendClientLog(payload) {
      return this.casesRestClient.request({
        url: 'client-logs/log',
        method: 'post',
        data: payload,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }
  }, {
    key: 'getPolicyCaseSummary',
    value: function getPolicyCaseSummary(caseHeaderId) {
      return this.casesRestClient.get('/policycase/' + caseHeaderId + '/summary');
    }
  }, {
    key: 'getHistoricalPolicyCaseSummary',
    value: function getHistoricalPolicyCaseSummary(caseId) {
      return this.casesRestClient.get('/policycase/versions/' + caseId + '/summary');
    }
  }, {
    key: 'getPolicySummary',
    value: function getPolicySummary(policyId) {
      return this.casesRestClient.get('/policy/' + policyId + '/summary');
    }
  }, {
    key: 'getPolicyDetails',
    value: function getPolicyDetails(policyId) {
      return this.casesRestClient.get('/policy/' + policyId + '/details');
    }
  }, {
    key: 'recalculatePolicyCase',
    value: function recalculatePolicyCase(payload, caseHeaderId) {
      return this.casesRestClient.request({
        url: '/policycase/' + caseHeaderId + '?action=recalculate',
        method: 'post',
        data: payload,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }
  }, {
    key: 'previewPolicyCase',
    value: function previewPolicyCase(payload, caseHeaderId) {
      return this.casesRestClient.request({
        url: '/policycase/' + caseHeaderId + '?action=preview',
        method: 'post',
        data: payload,
        headers: {
          'Content-type': 'application/json'
        }
      });
    }

    /**
    * Get all  Acord mapping table rows
    */

  }, {
    key: 'getAllAcordMappingRows',
    value: function getAllAcordMappingRows() {
      return this.casesRestClient.get('/acord_mapping');
    }

    /**
     * Create a new  Acord mapping table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'createAcordMappingRow',
    value: function createAcordMappingRow(row) {
      return this.casesRestClient.post('/acord_mapping', row);
    }

    /**
     * Update an existing  Acord mapping table row
     * @param row - { id, name, code, coverType, insurerName, percentage, startDate }
     */

  }, {
    key: 'updateAcordMappingRow',
    value: function updateAcordMappingRow(row) {
      return this.casesRestClient.put('/acord_mapping', row);
    }

    /**
     * Delete an existing  Acord mapping table row
     * @param id - of Acord mapping table row
     */

  }, {
    key: 'deleteAcordMappingRow',
    value: function deleteAcordMappingRow(id) {
      return this.casesRestClient.delete('/acord_mapping/' + id);
    }

    /**
    * Get gimobjects by productLineName and insurerName
    */

  }, {
    key: 'getGimobjectsByProductLineAndInsurer',
    value: function getGimobjectsByProductLineAndInsurer(productLineName, insurerName) {
      return this.casesRestClient.get('/gim-object/byProductLineAndInsurer?productLineName=' + productLineName + '&insurerName=' + insurerName);
    }
  }, {
    key: 'onLevelImport',
    value: function onLevelImport(policyId) {
      return this.casesRestClient.post('/level/' + policyId + '/calculate');
    }
  }]);

  return RestService;
}();

export default RestService;


// WEBPACK FOOTER //
// ./src/service/RestService.js